import { useState, useEffect, useCallback, ChangeEvent } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { ExceptionCase } from '../types';
import { displayUser } from '../utils';

const statusColors: Record<string, 'primary' | 'secondary' | 'default'> = {
  open: 'primary',
  approved: 'secondary',
  rejected: 'default',
  expired: 'default',
};

export const ExceptionCaseList = () => {
  const [cases, setCases] = useState<ExceptionCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const navigate = useNavigate();

  const fetchCases = useCallback(
    async (status?: string) => {
      try {
        setLoading(true);
        const baseUrl = await discoveryApi.getBaseUrl('exceptions');
        const url = status
          ? `${baseUrl}/cases?status=${status}`
          : `${baseUrl}/cases`;
        const response = await fetchApi.fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch cases: ${response.statusText}`);
        }
        const data = await response.json();
        setCases(data.cases || []);
        setError(null);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi],
  );

  useEffect(() => {
    fetchCases(statusFilter || undefined);
  }, [statusFilter, fetchCases]);

  const handleStatusFilterChange = (event: ChangeEvent<{ value: unknown }>) => {
    setStatusFilter(event.target.value as string);
    setPage(0);
  };

  const handleCaseClick = (caseId: string) => {
    navigate(`/exceptions/cases/${caseId}`);
  };

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  // A plain MUI table is used on purpose instead of the Backstage `Table`
  // (material-table): this app's root package.json forces `uuid` to 11.x
  // (resolutions["uuid@npm:^3.4.0"]), and @material-table/core calls
  // `uuid.default.v4()`, which uuid 11 no longer exports. Rendering any row
  // through that Table throws "Cannot read properties of undefined
  // (reading 'v4')". Revisit if that resolution is ever changed.
  const searchTerm = search.trim().toLocaleLowerCase();
  const filteredCases = searchTerm
    ? cases.filter(row =>
        [row.caseId, row.ban, row.requirementRef, row.requester, row.status]
          .filter(Boolean)
          .some(value =>
            String(value).toLocaleLowerCase().includes(searchTerm),
          ),
      )
    : cases;
  const lastPage = Math.max(
    0,
    Math.ceil(filteredCases.length / rowsPerPage) - 1,
  );
  const currentPage = Math.min(page, lastPage);
  const visibleCases = filteredCases.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" component="h2">
            Exception Cases
          </Typography>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 120 }}
          >
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box mb={2}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="search"
            label="Search cases"
            placeholder="Case ID, application, requirement, requester or status"
            value={search}
            onChange={event => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
        </Box>
        <TableContainer>
          <Table size="small" aria-label="Exception cases">
            <TableHead>
              <TableRow>
                <TableCell>Case ID</TableCell>
                <TableCell>Application</TableCell>
                <TableCell>Requirement</TableCell>
                <TableCell>Requester</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Messages</TableCell>
                <TableCell>Opened</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {cases.length === 0
                      ? 'No exception cases found.'
                      : 'No cases match your search.'}
                  </TableCell>
                </TableRow>
              ) : (
                visibleCases.map(row => (
                  <TableRow key={row.caseId} hover>
                    <TableCell>
                      <Button
                        color="primary"
                        variant="text"
                        onClick={() => handleCaseClick(row.caseId)}
                      >
                        {row.caseId}
                      </Button>
                    </TableCell>
                    <TableCell>{row.ban}</TableCell>
                    <TableCell>{row.requirementRef}</TableCell>
                    <TableCell>{displayUser(row.requester)}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        color={statusColors[row.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">{row.messageCount}</TableCell>
                    <TableCell>
                      {row.openedAt
                        ? new Date(row.openedAt).toLocaleDateString('en-GB')
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredCases.length}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={event => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </CardContent>
    </Card>
  );
};
